import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, FileCheck, Upload, FileText, Download, Trash2 } from "lucide-react";
import { formatApiError } from "@/auth/AuthContext";
import { inputCls, labelCls } from "@/components/ChildForm";
import { StatusChip } from "@/components/StaffDocuments";
import { SubsidiesCard } from "@/components/InvoiceParts";
import { useAuth } from "@/auth/AuthContext";
import { isFloorStaff } from "@/lib/roles";
import { FileLink } from "@/lib/files";

const RENEWAL_LABEL = { expired: "Expired", urgent: "Urgent", warning: "Expiring" };

function WinzRenewalsCard({ api }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => axios.get(`${api}/winz/renewals`).then((r) => setData(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const attention = data.rows.filter((r) => r.status !== "ok");
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] overflow-hidden" data-testid="winz-renewals-card">
      <div className="px-6 py-4 border-b border-[var(--hp-border)] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {data.needingRenewal === 0 ? <ShieldCheck className="h-5 w-5 text-[var(--hp-profit)]" /> : <ShieldAlert className="h-5 w-5 text-[var(--hp-gold)]" />}
          <div>
            <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Upcoming renewals</h3>
            <p className="text-xs text-[var(--hp-muted)]">Reminders {data.warnDays} days before a subsidy's approval ends, urgent at {data.urgentDays} days.</p>
          </div>
        </div>
        <span className="text-xs text-[var(--hp-muted)]" data-testid="winz-renewals-count">{data.rows.length} active subsid{data.rows.length === 1 ? "y" : "ies"}</span>
      </div>
      {attention.length === 0 ? (
        <p className="px-6 py-4 text-sm text-[var(--hp-profit)]" data-testid="winz-renewals-all-good">Nothing due for renewal.</p>
      ) : (
        <div className="px-6 py-4 space-y-2">
          {attention.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`winz-renewal-${r.id}`}>
              <StatusChip status={r.status} daysLeft={r.daysLeft}>{RENEWAL_LABEL[r.status] || r.status}</StatusChip>
              <span className="font-semibold text-[var(--hp-heading)]">{r.childName}</span>
              <span className="text-[var(--hp-muted)]">${r.weeklyAmount}/wk · {r.status === "expired" ? "ended" : "ends"} {r.end}{r.reference ? ` · ${r.reference}` : ""}</span>
              {!r.hasDocument && <span className="rounded-full border border-[var(--hp-gold)]/40 px-2 py-0.5 text-xs font-semibold text-[var(--hp-gold)]" data-testid={`winz-renewal-nodoc-${r.id}`}>No scanned form on file</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WinzDocumentsCard({ api }) {
  const [kids, setKids] = useState([]);
  const [childId, setChildId] = useState("");
  const [docs, setDocs] = useState([]);
  const [issued, setIssued] = useState("");
  const [expiry, setExpiry] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${api}/children`).then((r) => setKids(r.data)).catch(() => {}); }, [api]);
  const loadDocs = useCallback(() => {
    if (!childId) { setDocs([]); return; }
    axios.get(`${api}/children/${childId}/winz-documents`).then((r) => setDocs(r.data)).catch(() => {});
  }, [api, childId]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const upload = async () => {
    if (!childId) { toast.error("Choose a child first"); return; }
    if (!file && !issued && !expiry) { toast.error("Choose a file or enter a date"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("issuedDate", issued); fd.append("expiryDate", expiry); fd.append("note", note);
    if (file) fd.append("file", file);
    try {
      await axios.post(`${api}/children/${childId}/winz-documents`, fd);
      toast.success("WINZ form saved");
      setIssued(""); setExpiry(""); setNote(""); setFile(null);
      loadDocs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (d) => {
    if (!window.confirm(`Remove this document${d.filename ? ` (${d.filename})` : ""}?`)) return;
    try { await axios.delete(`${api}/winz-documents/${d.id}`); toast.success("Document removed"); loadDocs(); }
    catch (e) { toast.error("Failed to remove"); }
  };

  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-4" data-testid="winz-documents-card">
      <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2"><FileCheck className="h-4 w-4 text-[var(--hp-accent)]" /> Scanned WINZ forms</h3>
      <p className="text-xs text-[var(--hp-muted)]">Store the completed, signed WINZ / MSD OSCAR subsidy application or approval letter for each child.</p>
      <div className="max-w-sm">
        <label className={labelCls}>Child</label>
        <select data-testid="winz-doc-child" value={childId} onChange={(e) => setChildId(e.target.value)} className={inputCls}>
          <option value="">Select a child…</option>
          {kids.map((k) => <option key={k.id} value={k.id}>{k.fullName}{k.parentName ? ` (${k.parentName})` : ""}</option>)}
        </select>
      </div>
      {childId && (
        <>
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-[var(--hp-border)] bg-[var(--hp-bg)]/40 p-3" data-testid="winz-doc-upload-form">
            <label className="text-xs text-[var(--hp-muted)]">Issued<br /><input data-testid="winz-doc-issued" type="date" value={issued} onChange={(e) => setIssued(e.target.value)} className={inputCls} /></label>
            <label className="text-xs text-[var(--hp-muted)]">Expiry (optional)<br /><input data-testid="winz-doc-expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className={inputCls} /></label>
            <label className="text-xs text-[var(--hp-muted)]">Note<br /><input data-testid="winz-doc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" className={`${inputCls} w-40`} /></label>
            <label className="text-xs text-[var(--hp-muted)]">File (PDF / image / Word, max 10MB)<br />
              <input data-testid="winz-doc-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-xs text-[var(--hp-heading)] file:mr-2 file:rounded-full file:border-0 file:bg-[var(--hp-tint-10)] file:px-3 file:py-1 file:text-xs file:text-[var(--hp-heading)]" />
            </label>
            <button data-testid="winz-doc-upload-button" onClick={upload} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hp-primary)] px-4 py-1.5 text-xs font-bold text-[#04231a] hover:bg-[var(--hp-primary-hover)] disabled:opacity-50"><Upload className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save form"}</button>
          </div>
          {docs.length === 0 ? <p className="text-xs text-[var(--hp-muted)]" data-testid="winz-docs-empty">No WINZ documents stored for this child yet.</p> : (
            <table className="w-full text-xs" data-testid="winz-docs-list">
              <thead><tr className="text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)]"><th className="py-1.5 text-left">File</th><th className="py-1.5 text-left">Issued</th><th className="py-1.5 text-left">Expiry</th><th className="py-1.5 text-left">Note</th><th /></tr></thead>
              <tbody>{docs.map((d) => (
                <tr key={d.id} className="border-b border-[var(--hp-border)]/40" data-testid={`winz-doc-row-${d.id}`}>
                  <td className="py-1.5">{d.hasFile ? <FileLink data-testid={`winz-doc-download-${d.id}`} href={`${api}/winz-documents/${d.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--hp-accent)] hover:underline"><FileText className="h-3 w-3" />{d.filename}<Download className="h-3 w-3" /></FileLink> : <span className="text-[var(--hp-muted)]">record only</span>}</td>
                  <td className="py-1.5 text-[var(--hp-muted)]">{d.issuedDate || "—"}</td>
                  <td className="py-1.5 text-[var(--hp-muted)]">{d.expiryDate || "—"}</td>
                  <td className="py-1.5 text-[var(--hp-muted)]">{d.note || "—"}</td>
                  <td className="py-1.5 text-right"><button data-testid={`winz-doc-delete-${d.id}`} onClick={() => del(d)} className="text-[var(--hp-muted)] hover:text-[var(--hp-loss)]"><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

export default function WinzTab({ api }) {
  const { user } = useAuth();
  return (
    <div className="space-y-6" data-testid="winz-tab">
      <WinzRenewalsCard api={api} />
      <WinzDocumentsCard api={api} />
      {!isFloorStaff(user) && <SubsidiesCard api={api} />}
    </div>
  );
}
